<template>
    <div class="product" :class="{ visible: !discontinued, '--also-recommended': minimal }">
        <div class="product__image" :class="{ visible: product_image_loaded }">
            <a class="thumb-link" :href="details.product_link" :title="details.name">
                <img class="image" :src="details.product_photo" :alt="details.name" @error="imageLoadError" @load="imageLoadSuccess">
            </a>
        </div>

        <div class="product__details">
            <div class="product__title">
                <h3 class="product__brand">
                    <span v-if="details.brand">{{ details.brand }}<br></span>{{ details.name }}
                </h3>
            </div>

            <div class="description" v-if="!minimal" v-html="details.long_description" />

            <div class="size" v-if="orderable && !details.size_variations">Size: <span class="active">{{ details.c_size }}</span></div>

            <div class="size-variations size" v-if="orderable && details.size_variations">
                Size: <span v-for="(v, i) in details.size_variations" :class="{ active: v.upc === product_upc }" :key="i" style="margin-right: 10px;"><a @click="updateProduct(v.upc)">{{ v.size }}</a></span>
            </div>

            <div class="color-variations" v-if="details.color_variations">
                <ul>
                    <li v-for="(v, i) in details.color_variations" class="selectable" :class="{ selected: product_upc === v.upc }" :key="i">
                        <a @click="updateProduct(v.upc)">
                            <img :src="shade_img_src(v)" :alt="v.color">
                        </a>
                    </li>
                </ul>
            </div>

            <fieldset>
                <div class="product__add-to-cart">
                    <div class="price-qty-row">
                        <div class="price">
                            <span class="price-label">Price: </span><span class="price-sales">$ {{ details.price }}</span>
                        </div>
                        <div class="inventory" v-if="orderable">
                            <div class="quantity">
                                <span>Qty: </span>

                                <select title="Quantity" class="underline" v-model="qty">
                                    <option :value="1">1</option>
                                    <option :value="2">2</option>
                                    <option :value="3">3</option>
                                    <option :value="4">4</option>
                                    <option :value="5">5</option>
                                    <option :value="6">6</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div v-if="!orderable">This product is out of stock, but you can still wishlist it!</div>
                    <button @click="addToBag" id="add-to-cart" type="submit" title="Add to Bag" value="Add to Bag" class="button addToCart--composed">{{ orderable ? 'Add to Bag' : 'Add to Wishlist' }}</button>
                </div>
            </fieldset>
        </div>
    </div>
</template>

<script>
import { analytics, shopping, product } from 'shiseido-apis'
import Color from './color.vue'

export default {
    components: { Color },
    props: {
        upc: {
            type: String,
            default: () => '',
            required: true,
        },
        minimal: {
            type: Boolean,
            default: () => false,
        },
    },
    data() {
        return {
            product_upc: this.upc,
            details: {
                product_link: '',
                name: '',
                price: '',
                c_size: '',
                long_description: '',
                product_photo: '',
                color_variations: null,
                size_variations: null,
            },
            qty: 1,
            baseurl: '',
            baseimageurl: '',
            product_image_loaded: false,
            discontinued: true,
            orderable: false,
            errors: {
                removed_leading_0: false,
                added_leading_0: false,
            },
        }
    },
    created() {
        if (window.location.hostname.match(/(localhost|dev04|staging)/i)) {
            this.baseurl = '//staging-emea-shiseido.demandware.net'
            this.baseimageurl = '//sits-pod45.demandware.net'.concat('/dw/image/v2/BBSK_STG/on/demandware.static/-/Sites-itemmaster_shiseido/default/images/hi-res/')
        } else {
            this.baseurl = window.location.origin
            this.baseimageurl = window.location.origin.concat('/dw/image/v2/BBSK_PRD/on/demandware.static/-/Sites-itemmaster_shiseido/default/images/hi-res/')
        }

        this.fetchData()
    },
    methods: {
        fetchData() {
            product.getShiseidoProductDetails(this.product_upc)
                .then((response) => {
                    if (typeof response.fault === 'undefined') {
                        if (typeof response.variants === 'undefined') {
                            response.product_photo = response.image_groups.filter((g) => g.view_type === 'hi-res')[0].images[0].dis_base_link
                        } else if (response.variation_attributes.some((v) => v.name === 'Color')) {
                            response.color_variations = response.variants.filter((v) => v.orderable).map((v) => ({ color: v.variation_values.color, upc: v.product_id }))

                            this.product_upc = response.color_variations[0].upc

                            const hi_res_variants = response.image_groups.filter((g) => g.view_type === 'hi-res')

                            if (hi_res_variants.length > 0) {
                                const upc_matches = hi_res_variants.filter((g) => g.images.some((u) => u.dis_base_link.includes(this.product_upc.replace(/^0/, ''))))

                                if (upc_matches.length > 0) {
                                    response.product_photo = upc_matches[0].images[0].dis_base_link
                                }
                            } else {
                                response.product_photo = this.baseimageurl.concat(`${ this.product_upc }.jpg?sw=540&sh=540&sm=fit`)
                            }
                        } else if (response.variation_attributes.some((v) => v.name === 'Size')) {
                            response.size_variations = response.variants.filter((v) => v.orderable).map((v) => ({ size: v.variation_values.size, upc: v.product_id }))

                            this.product_upc = response.size_variations[0].upc

                            response.product_photo = response.image_groups.filter((g) => g.view_type === 'hi-res')[0].images[0].dis_base_link
                        }

                        response.product_link = '/on/demandware.store/Sites-shiseido_us-Site/en_US/Product-Show?pid='.concat(this.product_upc)

                        this.details = response
                        this.orderable = response.inventory.orderable

                        this.discontinued = false
                    }
                })
        },
        addToBag() {
            const quickviewurl = this.baseurl.concat(`/on/demandware.store/Sites-shiseido_us-Site/en_US/Product-Show?pid=${ this.product_upc }`)

            if (this.orderable) {
                shopping.addToCart(this.product_upc, this.qty)
            } else {
                shopping.addToWishlist(this.product_upc)
            }

            analytics.registerClick({
                toolname: 'Suncare Finder',
                element: 'Add To Bag',
                question: 'Results',
                answers: [quickviewurl],
            })
        },
        updateProduct(upc) {
            console.log('Update product to variation', upc)

            this.product_image_loaded = false
            this.product_upc = upc
            this.qty = 1
            this.errors = {
                removed_leading_0: false,
                added_leading_0: false,
            }

            // why: as far as I can tell, we cannot neatly handle a sub-string that's possibly one length or another
            if (this.details.product_photo.match(/[0-9]{13}/)) {
                this.details.product_photo = this.details.product_photo.replace(/[0-9]{13}/, upc)
            } else {
                this.details.product_photo = this.details.product_photo.replace(/[0-9]{12}/, upc)
            }
        },
        shade_img_src(variation) {
            return 'https://www.shiseido.com/dw/image/v2/BBSK_PRD/on/demandware.static/-/Sites-itemmaster_shiseido/default/images/swatch/'
                .concat(`${ variation.upc.replace(/^0+/, '') }.jpg?sw=52&sh=52`)
        },
        imageLoadError() {
            if (this.errors.removed_leading_0 && this.errors.added_leading_0) {
                return console.warn('Image error for', this.product_upc)
            }

            if (this.details.product_photo.match(/[0-9]{13}/)) {
                this.errors.removed_leading_0 = true
                this.details.product_photo = this.details.product_photo.replace(/[0-9]{13}/, this.product_upc.replace(/^0/, ''))

                return true
            }

            if (this.details.product_photo.match(/[0-9]{12}/)) {
                this.errors.added_leading_0 = true
                this.details.product_photo = this.details.product_photo.replace(/[0-9]{13}/, '0'.concat(this.product_upc))

                return true
            }
        },
        imageLoadSuccess() {
            this.product_image_loaded = true
        },
    },
}
</script>


// WEBPACK FOOTER //
// development/js/product.vue